// src/services/api.js
import axios from 'axios';
import { getToken } from './auth';
import { emitAuthSessionInvalidated } from './authEvents';

// Base URL for all API requests. Prefer an explicit environment variable
// but fall back to sensible defaults for local development and production
const API_BASE_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === 'production'
    ? 'https://virtualmedic.uk/api'
    : 'http://localhost:5000/api');

export const API_BASE = API_BASE_URL;

const normalizeMediaRelativePath = (value = '') => value
  .toString()
  .trim()
  .replace(/\\/g, '/')
  .replace(/^\/+/, '')
  .replace(/^(?:uploads|media)\//, '');

// Helper function to convert relative media paths to absolute URLs
export const mediaUrl = (rel) => {
  if (!rel) return null;
  if (/^https?:\/\//i.test(rel)) return rel;
  const cleanRel = normalizeMediaRelativePath(rel);
  return `${API_BASE_URL.replace(/\/api\/?$/, '')}/media/${cleanRel}`;
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('Failed to parse stored user', error);
    return null;
  }
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const currentUser = getStoredUser();
    if (currentUser?.universityId) {
      config.headers['x-tenant-id'] = currentUser.universityId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const unauthenticatedAuthPaths = [
  '/auth/login',
  '/auth/register',
  '/auth/session/exchange',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/verification-email',
  '/auth/psn/exchange',
  '/auth/psn/link/poll',
  '/auth/psn/link/create-account',
  '/auth/psn/web-access/claim',
];

const isUnauthenticatedAuthRequest = (url = '') => unauthenticatedAuthPaths
  .some((path) => url === path || url.startsWith(`${path}/`));

export const handleApiResponseError = (error) => {
  const status = error?.response?.status;
  const errorCode = error?.response?.data?.code || '';
  const requestUrl = error?.config?.url || '';
  const accountAccessRevoked = status === 403 && (
    errorCode === 'ACCOUNT_DEACTIVATED'
    || errorCode.startsWith('UNIVERSITY_')
    || errorCode === 'PSN_SESSION_REVOKED'
    || errorCode === 'PSN_LINK_REVOKED'
  );
  if ((status === 401 || accountAccessRevoked) && !isUnauthenticatedAuthRequest(requestUrl)) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    emitAuthSessionInvalidated({
      reason: errorCode || 'SESSION_EXPIRED',
    });
  }

  return Promise.reject(error);
};

api.interceptors.response.use(
  (response) => response,
  handleApiResponseError
);

// Authentication API endpoints
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  registerIndividual: (userData) => api.post('/auth/register/individual', userData),
  registerWithInvite: (userData) => api.post('/auth/register/invite', userData),
  registerWithDomain: (userData) => api.post('/auth/register/domain', userData),
  previewInvite: (token) => api.get(`/auth/register/invite/${encodeURIComponent(token)}`),
  registerWithJoinLink: (userData) => api.post('/auth/register/join', userData),
  previewJoinLink: (token) => api.get(`/auth/register/join/${encodeURIComponent(token)}`),
  getSignupExperienceConfig: () => api.get('/auth/signup-config'),
  login: (credentials) => api.post('/auth/login', credentials),
  exchangeSessionCode: (sessionCode) => api.post('/auth/session/exchange', { sessionCode }),
  verifyEmail: (token) => api.post(`/auth/verify-email/${token}`),
  resendVerificationEmail: (email) => api.post('/auth/verification-email/resend', { email }),
  requestPasswordReset: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
  validateToken: () => api.get('/auth/validate'),
  getLinkedProviders: () => api.get('/auth/oauth/providers/me'),
  unlinkProvider: (provider) => api.delete(`/auth/oauth/providers/${provider}`),
  startLinkSession: (provider, payload = {}) => api.post(`/auth/oauth/${provider}/link/start`, payload),
  getPsnLink: () => api.get('/auth/psn/link/me'),
  confirmPsnLink: (userCode) => api.post('/auth/psn/link/confirm', { userCode }),
  unlinkPsnLink: (payload = {}) => api.delete('/auth/psn/link/me', { data: payload }),
  claimPsnWebAccess: (payload) => api.post('/auth/psn/web-access/claim', payload),
};

// User-specific API endpoints
export const userAPI = {
  getCurrentUser: () => api.get('/users/profile'),
  updateProfile: (userData) => api.put('/users/profile', userData),
};

// History-related API endpoints
export const historyAPI = {
  getAllHistories: () => api.get('/histories'),
  getHistory: (id) => api.get(`/histories/${id}`),
  submitHistory: (data) => api.post('/histories', data),
};

export const caseVersionAPI = {
  list: (caseId, params = {}) => api.get(`/cases/${caseId}/versions`, { params }),
  getSnapshot: (caseId, versionId) => api.get(`/cases/${caseId}/versions/${versionId}`),
  getDiff: (caseId, versionId, params = {}) => api.get(`/cases/${caseId}/versions/${versionId}/diff`, { params }),
  downloadSnapshot: (caseId, versionId) => api.get(`/cases/${caseId}/versions/${versionId}/download`, { responseType: 'blob' }),
  restore: (caseId, versionId, payload = {}) => api.post(`/cases/${caseId}/versions/${versionId}/restore`, payload)
};

export const backupAPI = {
  getStatus: () => api.get('/admin/backups/status'),
  listJobs: (params = {}) => api.get('/admin/backups', { params }),
  triggerManual: (payload = {}) => api.post('/admin/backups/manual', payload),
  triggerRestoreDrill: (payload = {}) => api.post('/admin/backups/restore-drill', payload),
  queueExport: (payload = {}) => api.post('/admin/backups/exports', payload),
  listExports: (params = {}) => api.get('/admin/backups/exports', { params }),
  refreshExportDownload: (jobId) => api.get(`/admin/backups/exports/${jobId}/download`)
};

export const virtualMedicAPI = {
  listLibraryCases: (params = {}) => api.get('/virtual-medic/cases', { params }),
  getLibraryCase: (caseId) => api.get(`/virtual-medic/cases/${caseId}`),
  importCase: (caseId, payload = {}) => api.post(`/virtual-medic/cases/${caseId}/import`, payload),
};

export const communityAPI = {
  listCases: (params = {}) => api.get('/community/cases', { params }),
  getCase: (caseId) => api.get(`/community/cases/${caseId}`),
  publishCase: (caseId, payload = {}) => api.post(`/community/cases/${caseId}/publish`, payload),
  withdrawCase: (caseId) => api.post(`/community/cases/${caseId}/withdraw`),
  approveCase: (caseId, payload = {}) => api.post(`/community/cases/${caseId}/approve`, payload),
  rejectCase: (caseId, payload = {}) => api.post(`/community/cases/${caseId}/reject`, payload),
  importCase: (caseId) => api.post(`/community/cases/${caseId}/import`),
  forkCase: (caseId) => api.post(`/community/cases/${caseId}/fork`),
  listComments: (caseId, params = {}) => api.get(`/community/cases/${caseId}/comments`, { params }),
  createComment: (caseId, payload = {}) => api.post(`/community/cases/${caseId}/comments`, payload),
  rateCase: (caseId, payload = {}) => api.post(`/community/cases/${caseId}/ratings`, payload),
  reportCase: (caseId, payload = {}) => api.post(`/community/cases/${caseId}/report`, payload),
};

export const groupAPI = {
  list: () => api.get('/groups'),
  create: (payload) => api.post('/groups', payload),
  update: (groupId, payload) => api.put(`/groups/${groupId}`, payload),
  archive: (groupId) => api.delete(`/groups/${groupId}`),
  assignMembers: (groupId, payload = []) => api.post(
    `/groups/${groupId}/members`,
    Array.isArray(payload) ? { memberIds: payload } : payload,
  )
};

export const learningAPI = {
  listStudents: (params = {}) => api.get('/learning/students', { params }),
  getStudentAnalytics: (studentId) => api.get(`/learning/students/${studentId}/analytics`),
  listStudentHistories: (studentId, params = {}) => api.get(`/learning/students/${studentId}/histories`, { params }),
  getGroup: (groupId) => api.get(`/learning/groups/${groupId}`),
  listAssignments: (params = {}) => api.get('/learning/assignments', { params }),
  createAssignment: (payload = {}) => api.post('/learning/assignments', payload),
  archiveAssignment: (assignmentId) => api.delete(`/learning/assignments/${assignmentId}`),
  listMyAssignments: () => api.get('/learning/me/assignments'),
  listAnnouncements: () => api.get('/learning/announcements'),
  getAnnouncement: (announcementId) => api.get(`/learning/announcements/${announcementId}`),
  createAnnouncement: (payload = {}) => api.post('/learning/announcements', payload),
  archiveAnnouncement: (announcementId) => api.delete(`/learning/announcements/${announcementId}`),
  getHistoryFeedback: (historyId) => api.get(`/learning/histories/${historyId}/feedback`),
  addHistoryFeedbackComment: (historyId, body) => api.post(`/learning/histories/${historyId}/feedback/comments`, { body }),
  updateHistoryFeedbackStatus: (historyId, status) => api.patch(`/learning/histories/${historyId}/feedback/status`, { status }),
};

export const notificationAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  markRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

export const tenantAdminAPI = {
  dashboard: () => api.get('/tenant-admin/dashboard'),
  listMembers: (params = {}) => api.get('/tenant-admin/members', { params }),
  updateMemberRoles: (payload = {}) => api.post('/tenant-admin/members/roles', payload),
  assignMembersToGroups: (payload = {}) => api.post('/tenant-admin/members/groups', payload),
  listRosterImports: () => api.get('/tenant-admin/roster'),
  importRoster: (payload) => api.post('/tenant-admin/roster/import', payload),
  downloadRosterErrors: (importId) => api.get(`/tenant-admin/roster/${importId}/errors.csv`, { responseType: 'blob' }),
  commitRosterImport: (importId, payload) => api.post(`/tenant-admin/roster/${importId}/commit`, payload),
  sendRosterInvites: (importId) => api.post(`/tenant-admin/roster/${importId}/invites/send`),
  getCreditOverview: () => api.get('/tenant-admin/credits/overview'),
  updateCreditAllocationMode: (payload = {}) => api.put('/tenant-admin/credits/allocation-mode', payload),
  listCreditInvoices: (params = {}) => api.get('/tenant-admin/credits/invoices', { params }),
  requestCreditInvoice: (payload = {}) => api.post('/tenant-admin/credits/invoices', payload),
  downloadCreditInvoicePdf: (invoiceId) => api.get(`/tenant-admin/credits/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  allocateCredits: (payload = {}) => api.post('/tenant-admin/credits/allocate', payload),
  getAiConfiguration: () => api.get('/tenant-admin/ai-config'),
  updateAiConfiguration: (payload) => api.put('/tenant-admin/ai-config', payload),
  getRegistrationPolicy: () => api.get('/tenant-admin/registration/policy'),
  listAllowlistEntries: (params = {}) => api.get('/tenant-admin/registration/allowlist', { params }),
  addAllowlistEntry: (payload = {}) => api.post('/tenant-admin/registration/allowlist', payload),
  removeAllowlistEntry: (entryId) => api.delete(`/tenant-admin/registration/allowlist/${entryId}`),
  listJoinLinks: () => api.get('/tenant-admin/join-links'),
  getJoinLinkBranding: () => api.get('/tenant-admin/join-links/branding'),
  createJoinLink: (payload = {}) => api.post('/tenant-admin/join-links', payload),
  updateJoinLink: (linkId, payload = {}) => api.patch(`/tenant-admin/join-links/${linkId}`, payload),
  rotateJoinLink: (linkId) => api.post(`/tenant-admin/join-links/${linkId}/rotate`),
  revokeJoinLink: (linkId) => api.post(`/tenant-admin/join-links/${linkId}/revoke`),
  getUsagePolicy: () => api.get('/tenant-admin/usage-policy'),
  updateUsagePolicy: (payload = {}) => api.put('/tenant-admin/usage-policy', payload),
  getCaseManagementSettings: () => api.get('/tenant-admin/case-management'),
  updateCaseManagementSettings: (payload = {}) => api.put('/tenant-admin/case-management', payload),
  adjustTokens: (payload = {}) => api.post('/tenant-admin/tokens/bulk-adjust', payload),
};

export const inviteAPI = {
  list: (params = {}) => api.get('/invites', { params }),
  create: (payload = {}) => api.post('/invites', payload),
  resend: (inviteId, payload = {}) => api.post(`/invites/${inviteId}/resend`, payload),
  revoke: (inviteId, payload = {}) => api.post(`/invites/${inviteId}/revoke`, payload),
  summary: (params = {}) => api.get('/invites/stats/summary', { params }),
};

export const analyticsAPI = {
  performance: (params = {}) => api.get('/analytics/performance', { params }),
  snapshots: (params = {}) => api.get('/analytics/performance/snapshots', { params }),
  refreshSnapshot: (payload = {}) => api.post('/analytics/performance/snapshots/refresh', payload),
  exportPerformance: (params = {}) => api.get('/analytics/performance/export', {
    params,
    responseType: 'blob'
  }),
  listViews: () => api.get('/analytics/views'),
  createView: (payload) => api.post('/analytics/views', payload),
  updateView: (viewId, payload) => api.put(`/analytics/views/${viewId}`, payload),
  deleteView: (viewId) => api.delete(`/analytics/views/${viewId}`),
  listDigests: () => api.get('/analytics/digests'),
  saveDigest: (payload) => api.post('/analytics/digests', payload),
  deleteDigest: (subscriptionId) => api.delete(`/analytics/digests/${subscriptionId}`),
  triggerDigest: (subscriptionId, params = {}) => api.post(`/analytics/digests/${subscriptionId}/send-now`, params)
};

export const licenseAPI = {
  getUsage: (universityId) => api.get(`/universities/${universityId}/license-usage`),
};

export const billingAPI = {
  getOverview: () => api.get('/billing/overview'),
  getStatus: () => api.get('/billing/status'),
  createPortalSession: (payload = {}) => api.post('/billing/portal/session', payload),
  startTokenPackCheckout: (payload = {}) => api.post('/billing/checkout/token-pack', payload),
  reconcileCheckoutSession: (payload = {}) => api.post('/billing/checkout/reconcile', payload),
  createCreditCheckout: (payload = {}) => api.post('/billing/checkout/credits', payload),
};

export const adminBillingAPI = {
  getUpsellInsights: (params = {}) => api.get('/admin/billing/upsell-insights', { params }),
  listUniversityQuotes: (universityId, params = {}) => api.get(`/admin/universities/${universityId}/quotes`, { params }),
  createQuote: (universityId, payload) => api.post(`/admin/universities/${universityId}/quotes`, payload),
  updateQuote: (quoteId, payload) => api.patch(`/admin/quotes/${quoteId}`, payload),
  updateQuoteStatus: (quoteId, payload) => api.post(`/admin/quotes/${quoteId}/status`, payload),
};

const signAction = async (payload = {}) => {
  const { data } = await api.post('/global-admin/actions/sign', payload);
  return data?.token;
};

export const globalAdminAPI = {
  listTenants: (params = {}) => api.get('/global-admin/tenants', { params }),
  getAlerts: (params = {}) => api.get('/global-admin/alerts', { params }),
  listActionLogs: (params = {}) => api.get('/global-admin/actions', { params }),
  getRunbooks: () => api.get('/global-admin/runbooks'),
  getSystemSettings: () => api.get('/global-admin/system-settings'),
  updateBillingSettings: (payload = {}) => api.put('/global-admin/system-settings/billing', payload),
  updatePilotSettings: (payload = {}) => api.put('/global-admin/system-settings/pilot', payload),
  updateWebPlayerSettings: (payload = {}) => api.put('/global-admin/system-settings/web-player', payload),
  updateOpenAiSettings: (payload = {}) => api.put('/global-admin/system-settings/openai', payload),
  getUnityAddressables: (params = {}) => api.get('/global-admin/system-settings/unity-addressables', { params }),
  uploadUnityAddressables: (payload = {}) => {
    const formData = new FormData();
    if (payload.file) {
      formData.append('file', payload.file);
    }
    if (payload.platform) {
      formData.append('platform', payload.platform);
    }
    if (payload.version) {
      formData.append('version', payload.version);
    }
    if (payload.buildNumber) {
      formData.append('buildNumber', payload.buildNumber);
    }
    if (payload.notes) {
      formData.append('notes', payload.notes);
    }
    return api.post('/global-admin/system-settings/unity-addressables', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateUnityAddressablesBuild: (buildId, payload = {}) => api.patch(`/global-admin/system-settings/unity-addressables/${buildId}`, payload),
  activateUnityAddressablesBuild: (buildId) => api.post(`/global-admin/system-settings/unity-addressables/${buildId}/activate`),
  deleteUnityAddressablesBuild: (buildId) => api.delete(`/global-admin/system-settings/unity-addressables/${buildId}`),
  getVoiceSamples: () => api.get('/global-admin/system-settings/voice-samples'),
  uploadVoiceSample: (payload = {}) => {
    const formData = new FormData();
    if (payload.provider) {
      formData.append('provider', payload.provider);
    }
    if (payload.voice) {
      formData.append('voice', payload.voice);
    }
    if (payload.file) {
      formData.append('file', payload.file);
    }
    return api.post('/global-admin/system-settings/voice-samples', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteVoiceSample: (provider, voice) => api.delete(`/global-admin/system-settings/voice-samples/${encodeURIComponent(provider)}/${encodeURIComponent(voice)}`),
  getFeatureChecklist: () => api.get('/global-admin/system-settings/features'),
  adjustLicenseSeats: async (licenseId, payload = {}) => {
    const signature = await signAction({
      action: 'adjust-license',
      targetId: licenseId,
      tenantId: payload.tenantId,
      metadata: { seatDelta: payload.seatDelta, seatLimit: payload.seatLimit },
    });
    return api.post(`/global-admin/licenses/${licenseId}/seat-limit`, {
      ...payload,
      signature,
    });
  },
  updateLicenseStatus: async (licenseId, payload = {}) => {
    const signature = await signAction({
      action: 'license-status',
      targetId: licenseId,
      tenantId: payload.tenantId,
      metadata: { status: payload.status },
    });
    return api.post(`/global-admin/licenses/${licenseId}/status`, {
      ...payload,
      signature,
    });
  },
  revokeTenantAccess: async (tenantId, payload = {}) => {
    const signature = await signAction({
      action: 'tenant-revoke',
      targetId: tenantId,
      tenantId,
      metadata: { reason: payload.reason },
    });
    return api.post(`/global-admin/tenants/${tenantId}/revoke-access`, {
      ...payload,
      signature,
    });
  },
  resetTenantSchedules: async (tenantId, payload = {}) => {
    const signature = await signAction({
      action: 'reset-schedules',
      targetId: tenantId,
      tenantId,
      metadata: { reason: payload.reason },
    });
    return api.post(`/global-admin/tenants/${tenantId}/reset-schedules`, {
      ...payload,
      signature,
    });
  },
  recordDownloadAnomaly: (payload = {}) => api.post('/global-admin/alerts/download', payload),
  createUniversity: (payload = {}) => api.post('/global-admin/universities', payload),
  getUniversity: (universityId) => api.get(`/global-admin/universities/${universityId}`),
  updateUniversity: (universityId, payload = {}) => api.put(`/global-admin/universities/${universityId}`, payload),
  archiveUniversity: (universityId, payload = {}) => api.post(`/global-admin/universities/${universityId}/archive`, payload),
  getUniversityAiConfig: (universityId) => api.get(`/global-admin/universities/${universityId}/ai-config`),
  updateUniversityAiConfig: (universityId, payload = {}) => api.put(`/global-admin/universities/${universityId}/ai-config`, payload),
  assignUniversityAdmin: (universityId, payload = {}) => inviteAPI.create({
    universityId,
    role: 'admin',
    email: payload.email,
    firstName: payload.firstName,
    lastName: payload.lastName,
    metadata: {
      source: 'global-admin-console',
      assignedBy: payload.assignedBy || null,
    },
  }),
  resendInvite: (inviteId, payload = {}) => inviteAPI.resend(inviteId, payload),
  revokeInvite: (inviteId, payload = {}) => inviteAPI.revoke(inviteId, payload),
  getFinanceOverview: () => api.get('/global-admin/compliance/finance'),
  exportFinanceReport: () => api.get('/global-admin/compliance/finance/export', { responseType: 'blob' }),
  listTransferCases: (params = {}) => api.get('/global-admin/case-transfer/cases', { params }),
  exportTransferCase: (caseId) => api.get(`/global-admin/case-transfer/cases/${caseId}/package`, { responseType: 'blob' }),
  deleteTransferCase: (caseId) => api.delete(`/global-admin/case-transfer/cases/${caseId}`),
  importTransferCase: (payload = {}) => {
    const formData = new FormData();
    if (payload.file) {
      formData.append('file', payload.file);
    }
    if (payload.targetEnvironment) {
      formData.append('targetEnvironment', payload.targetEnvironment);
    }
    if (payload.conflictStrategy) {
      formData.append('conflictStrategy', payload.conflictStrategy);
    }
    return api.post('/global-admin/case-transfer/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listCreditInvoices: (params = {}) => api.get('/global-admin/credit-invoices', { params }),
  createCreditInvoice: (payload = {}) => api.post('/global-admin/credit-invoices', payload),
  sendCreditInvoice: (invoiceId) => api.post(`/global-admin/credit-invoices/${invoiceId}/send`),
  markCreditInvoicePaid: (invoiceId) => api.post(`/global-admin/credit-invoices/${invoiceId}/mark-paid`),
  downloadCreditInvoicePdf: (invoiceId) => api.get(`/global-admin/credit-invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
};

export default api;
